import React from 'react';
import Card from 'react-bootstrap/Card';
import { createEmptyRecord, RESPONSE_VALUES } from './ConsolidatedFamilyHistoryTab';

const normalizeValue = (value) => {
    if (value === RESPONSE_VALUES.NA) return 'N/A';
    if (value === RESPONSE_VALUES.UNKNOWN) return 'Unknown';
    return value;
};

const isChecked = (uiValue, storedValue) => {
    if (uiValue === RESPONSE_VALUES.UNKNOWN && storedValue === 'Unknown') return true;
    if (uiValue === RESPONSE_VALUES.NA && storedValue === 'N/A') return true;
    return storedValue === uiValue;
};

const PatientMedicalDetailsCard = ({ medicalHistory, onFieldChange, disabled = false }) => {
    const handleChange = (historyItem, field, value) => {
        onFieldChange(historyItem, { [field]: value });
    };

    const handleRadioChange = (historyItem, value) => {
        const normalized = normalizeValue(value);
        onFieldChange(historyItem, { value: normalized });
    };

    const handleCheckboxChange = (historyItem, checked) => {
        onFieldChange(historyItem, { value: checked ? 'Yes' : 'No' });
    };

    const handleFlagToggle = (historyItem, currentFlagValue) => {
        if (!disabled) {
            handleChange(historyItem, 'flagYN', currentFlagValue === 1 ? 0 : 1);
        }
    };

    // Helper to check if value is "Yes"
    const isYes = (value) => value === 'Yes' || value === RESPONSE_VALUES.YES;
    const isNo = (value) => value === 'No' || value === RESPONSE_VALUES.NO;

    return (
        <Card className="pinkCard mb-3">
            <Card.Header>
                <span>PATIENT MEDICAL DETAILS</span>
            </Card.Header>
            <Card.Body>
                <h4><strong><em>Answer the following</em></strong></h4>

                {/* Hormone Replacement Therapy */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Have you taken Hormone Replacement Therapy (HRT)?
                        {isYes(medicalHistory.Hormones?.value) && (
                            <i
                                className={`fas fa-flag ml-2 ${medicalHistory.Hormones?.flagYN === 1 ? 'color-pink' : 'color-light-grey'} ${!disabled ? 'flag-clickable' : ''}`}
                                onClick={() => handleFlagToggle('Hormones', medicalHistory.Hormones?.flagYN)}
                                role={disabled ? undefined : "button"}
                                tabIndex={disabled ? undefined : 0}
                                title={medicalHistory.Hormones?.flagYN === 1 ? 'Remove flag' : 'Add flag'}
                            />
                        )}
                    </label>
                    <div className="mt-2">
                        {['Never', 'Stopped use 5 or more years ago', 'Stopped use less than 5 years ago', 'Current User'].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`Hormones-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="Hormones"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleRadioChange('Hormones', e.target.value)}
                                    checked={medicalHistory.Hormones?.value === value}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`Hormones-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* HRT Details - show if not "Never" */}
                {medicalHistory.Hormones?.value && medicalHistory.Hormones?.value !== 'Never' && (
                    <div className="row mb-4">
                        <div className="col-lg-4">
                            <label className="form-label">Estrogen only or combined treatment?</label>
                            <select
                                className="form-control-custom"
                                value={medicalHistory.Hormones?.typeOfHRT || ''}
                                onChange={(e) => handleChange('Hormones', 'typeOfHRT', e.target.value)}
                                disabled={disabled}
                            >
                                <option value="">Select...</option>
                                <option value="Estrogen only">Estrogen only</option>
                                <option value="Combined">Combined</option>
                            </select>
                        </div>
                        <div className="col-lg-4">
                            <label className="form-label">Length of HRT Use?</label>
                            <select
                                className="form-control-custom"
                                value={medicalHistory.Hormones?.lengthOfHRTUse || ''}
                                onChange={(e) => handleChange('Hormones', 'lengthOfHRTUse', e.target.value)}
                                disabled={disabled}
                            >
                                <option value="">Select...</option>
                                <option value="Less than 5 years">Less than 5 years</option>
                                <option value="5-10 years">5-10 years</option>
                                <option value="More than 10 years">More than 10 years</option>
                            </select>
                        </div>
                        <div className="col-lg-4">
                            <label className="form-label">How long has it been since you used HRT?</label>
                            <select
                                className="form-control-custom"
                                value={medicalHistory.Hormones?.timeSinceHRTUse || ''}
                                onChange={(e) => handleChange('Hormones', 'timeSinceHRTUse', e.target.value)}
                                disabled={disabled}
                            >
                                <option value="">Select...</option>
                                <option value="Current user">Current user</option>
                                <option value="Less than 1 year">Less than 1 year</option>
                                <option value="1-5 years">1-5 years</option>
                                <option value="More than 5 years">More than 5 years</option>
                            </select>
                        </div>
                    </div>
                )}

                {/* Oral Contraceptives */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Is the patient taking oral contraceptives?
                        {isYes(medicalHistory.OralContraceptives?.value) && (
                            <i
                                className={`fas fa-flag ml-2 ${medicalHistory.OralContraceptives?.flagYN === 1 ? 'color-pink' : 'color-light-grey'} ${!disabled ? 'flag-clickable' : ''}`}
                                onClick={() => handleFlagToggle('OralContraceptives', medicalHistory.OralContraceptives?.flagYN)}
                                role={disabled ? undefined : "button"}
                                tabIndex={disabled ? undefined : 0}
                                title={medicalHistory.OralContraceptives?.flagYN === 1 ? 'Remove flag' : 'Add flag'}
                            />
                        )}
                    </label>
                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`OralContraceptives-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="OralContraceptives"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleRadioChange('OralContraceptives', e.target.value)}
                                    checked={isChecked(value, medicalHistory.OralContraceptives?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`OralContraceptives-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Last Menstrual Period */}
                <div className="form-group mb-4">
                    <label className="form-label">When was the patient's last menstrual period?</label>
                    <input
                        type="date"
                        className="form-control-custom"
                        value={medicalHistory.DateofLastMenstrualPeriod?.date || ''}
                        onChange={(e) => handleChange('DateofLastMenstrualPeriod', 'date', e.target.value)}
                        disabled={disabled}
                    />
                </div>

                {/* Pregnant */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Is the patient pregnant?
                        {isYes(medicalHistory.PatientPregnant?.value) && (
                            <i
                                className={`fas fa-flag ml-2 ${medicalHistory.PatientPregnant?.flagYN === 1 ? 'color-pink' : 'color-light-grey'} ${!disabled ? 'flag-clickable' : ''}`}
                                onClick={() => handleFlagToggle('PatientPregnant', medicalHistory.PatientPregnant?.flagYN)}
                                role={disabled ? undefined : "button"}
                                tabIndex={disabled ? undefined : 0}
                                title={medicalHistory.PatientPregnant?.flagYN === 1 ? 'Remove flag' : 'Add flag'}
                            />
                        )}
                    </label>
                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`PatientPregnant-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="PatientPregnant"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleRadioChange('PatientPregnant', e.target.value)}
                                    checked={isChecked(value, medicalHistory.PatientPregnant?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`PatientPregnant-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Other Types of Cancer */}
                <div className="form-group mb-4">
                    <label className="form-label">Has the patient had other types of cancer?</label>
                    <div className="row">
                        <div className="col-auto">
                            <div className="custom-control custom-checkbox">
                                <input
                                    type="checkbox"
                                    id="OtherTypesofCancer-No"
                                    className="custom-control-input"
                                    checked={medicalHistory.OtherTypesofCancer?.value?.includes('No') || false}
                                    onChange={(e) => handleCheckboxChange('OtherTypesofCancer', false)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor="OtherTypesofCancer-No">
                                    No
                                </label>
                            </div>
                        </div>
                        <div className="col-auto">
                            <div className="custom-control custom-checkbox">
                                <input
                                    type="checkbox"
                                    id="OtherTypesofCancer-Ovaries"
                                    className="custom-control-input"
                                    checked={medicalHistory.OtherTypesofCancer?.typeOfCancer?.includes('Ovaries') || false}
                                    onChange={(e) => {
                                        const current = medicalHistory.OtherTypesofCancer?.typeOfCancer || '';
                                        const types = current.split(',').filter(t => t.trim());
                                        if (e.target.checked) {
                                            types.push('Ovaries');
                                        } else {
                                            const index = types.indexOf('Ovaries');
                                            if (index > -1) types.splice(index, 1);
                                        }
                                        handleChange('OtherTypesofCancer', 'typeOfCancer', types.join(','));
                                        handleChange('OtherTypesofCancer', 'value', types.length > 0 ? 'Yes' : 'No');
                                    }}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor="OtherTypesofCancer-Ovaries">
                                    Ovaries
                                </label>
                            </div>
                        </div>
                        <div className="col-auto">
                            <div className="custom-control custom-checkbox">
                                <input
                                    type="checkbox"
                                    id="OtherTypesofCancer-Uterus"
                                    className="custom-control-input"
                                    checked={medicalHistory.OtherTypesofCancer?.typeOfCancer?.includes('Uterus') || false}
                                    onChange={(e) => {
                                        const current = medicalHistory.OtherTypesofCancer?.typeOfCancer || '';
                                        const types = current.split(',').filter(t => t.trim());
                                        if (e.target.checked) {
                                            types.push('Uterus');
                                        } else {
                                            const index = types.indexOf('Uterus');
                                            if (index > -1) types.splice(index, 1);
                                        }
                                        handleChange('OtherTypesofCancer', 'typeOfCancer', types.join(','));
                                        handleChange('OtherTypesofCancer', 'value', types.length > 0 ? 'Yes' : 'No');
                                    }}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor="OtherTypesofCancer-Uterus">
                                    Uterus
                                </label>
                            </div>
                        </div>
                        <div className="col-auto">
                            <div className="custom-control custom-checkbox">
                                <input
                                    type="checkbox"
                                    id="OtherTypesofCancer-Other"
                                    className="custom-control-input"
                                    checked={medicalHistory.OtherTypesofCancer?.typeOfCancer?.includes('Other') || false}
                                    onChange={(e) => {
                                        const current = medicalHistory.OtherTypesofCancer?.typeOfCancer || '';
                                        const types = current.split(',').filter(t => t.trim());
                                        if (e.target.checked) {
                                            types.push('Other');
                                        } else {
                                            const index = types.indexOf('Other');
                                            if (index > -1) types.splice(index, 1);
                                        }
                                        handleChange('OtherTypesofCancer', 'typeOfCancer', types.join(','));
                                        handleChange('OtherTypesofCancer', 'value', types.length > 0 ? 'Yes' : 'No');
                                    }}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor="OtherTypesofCancer-Other">
                                    Other
                                </label>
                            </div>
                        </div>
                    </div>
                    {medicalHistory.OtherTypesofCancer?.typeOfCancer?.includes('Other') && (
                        <div className="mt-2">
                            <label className="form-label">Explain</label>
                            <input
                                type="text"
                                className="form-control-custom"
                                value={medicalHistory.OtherTypesofCancer?.notes || ''}
                                onChange={(e) => handleChange('OtherTypesofCancer', 'notes', e.target.value)}
                                disabled={disabled}
                            />
                        </div>
                    )}
                </div>

                {/* Height and Weight */}
                <div className="row mb-4">
                    <div className="col-lg-2">
                        <label className="form-label">Feet</label>
                        <input
                            type="number"
                            className="form-control-custom"
                            value={medicalHistory.Height?.value?.split("'")[0] || ''}
                            onChange={(e) => {
                                const feet = e.target.value;
                                const inches = medicalHistory.Height?.value?.split("'")[1]?.replace('"', '') || '0';
                                handleChange('Height', 'value', `${feet}'${inches}"`);
                            }}
                            min="0"
                            max="8"
                            disabled={disabled}
                        />
                    </div>
                    <div className="col-lg-2">
                        <label className="form-label">Inches</label>
                        <input
                            type="number"
                            className="form-control-custom"
                            value={medicalHistory.Height?.value?.split("'")[1]?.replace('"', '') || ''}
                            onChange={(e) => {
                                const feet = medicalHistory.Height?.value?.split("'")[0] || '0';
                                const inches = e.target.value;
                                handleChange('Height', 'value', `${feet}'${inches}"`);
                            }}
                            min="0"
                            max="11"
                            disabled={disabled}
                        />
                    </div>
                    <div className="col-lg-4">
                        <label className="form-label">Weight</label>
                        <input
                            type="number"
                            className="form-control-custom"
                            value={medicalHistory.Weight?.value || ''}
                            onChange={(e) => handleChange('Weight', 'value', e.target.value)}
                            min="0"
                            disabled={disabled}
                        />
                    </div>
                </div>

                {/* Age at First Menstrual Period */}
                <div className="form-group mb-4">
                    <label className="form-label">What was your age at the time of your first menstrual period?</label>
                    <input
                        type="number"
                        className="form-control-custom"
                        style={{ maxWidth: '150px' }}
                        value={medicalHistory.AgeatFirstMenstrualPeriod?.ageAtDiagnosis || ''}
                        onChange={(e) => handleChange('AgeatFirstMenstrualPeriod', 'ageAtDiagnosis', e.target.value)}
                        min="0"
                        max="30"
                        disabled={disabled}
                    />
                </div>

                {/* Given Birth */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Have you given birth to one or more children?
                        {isYes(medicalHistory.GivenBirth?.value) && (
                            <i
                                className={`fas fa-flag ml-2 ${medicalHistory.GivenBirth?.flagYN === 1 ? 'color-pink' : 'color-light-grey'} ${!disabled ? 'flag-clickable' : ''}`}
                                onClick={() => handleFlagToggle('GivenBirth', medicalHistory.GivenBirth?.flagYN)}
                                role={disabled ? undefined : "button"}
                                tabIndex={disabled ? undefined : 0}
                                title={medicalHistory.GivenBirth?.flagYN === 1 ? 'Remove flag' : 'Add flag'}
                            />
                        )}
                    </label>
                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`GivenBirth-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="GivenBirth"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleRadioChange('GivenBirth', e.target.value)}
                                    checked={isChecked(value, medicalHistory.GivenBirth?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`GivenBirth-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Age when delivered first child */}
                {isYes(medicalHistory.GivenBirth?.value) && (
                    <div className="form-group mb-4 ml-4">
                        <label className="form-label">How old were you when you delivered your first child?</label>
                        <input
                            type="number"
                            className="form-control-custom"
                            style={{ maxWidth: '150px' }}
                            value={medicalHistory.GivenBirth?.ageAtDiagnosis || ''}
                            onChange={(e) => handleChange('GivenBirth', 'ageAtDiagnosis', e.target.value)}
                            min="0"
                            max="60"
                            disabled={disabled}
                        />
                    </div>
                )}

                {/* Menopause */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Have you gone through menopause (1 year after last menstrual cycle)?
                        {isYes(medicalHistory.Menopause?.value) && (
                            <i
                                className={`fas fa-flag ml-2 ${medicalHistory.Menopause?.flagYN === 1 ? 'color-pink' : 'color-light-grey'} ${!disabled ? 'flag-clickable' : ''}`}
                                onClick={() => handleFlagToggle('Menopause', medicalHistory.Menopause?.flagYN)}
                                role={disabled ? undefined : "button"}
                                tabIndex={disabled ? undefined : 0}
                                title={medicalHistory.Menopause?.flagYN === 1 ? 'Remove flag' : 'Add flag'}
                            />
                        )}
                    </label>
                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`Menopause-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="Menopause"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleRadioChange('Menopause', e.target.value)}
                                    checked={isChecked(value, medicalHistory.Menopause?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`Menopause-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* At what age was menopause */}
                {isYes(medicalHistory.Menopause?.value) && (
                    <div className="form-group mb-4 ml-4">
                        <label className="form-label">At what age was menopause?</label>
                        <input
                            type="number"
                            className="form-control-custom"
                            style={{ maxWidth: '150px' }}
                            value={medicalHistory.Menopause?.ageAtDiagnosis || ''}
                            onChange={(e) => handleChange('Menopause', 'ageAtDiagnosis', e.target.value)}
                            min="0"
                            max="70"
                            disabled={disabled}
                        />
                    </div>
                )}

                {/* Genetic Mutation */}
                <div className="form-group mb-4">
                    <label className="form-label">Do you have a genetic mutation that puts you at a higher risk for developing breast cancer?</label>
                    <div className="mt-2">
                        {['Unknown', 'Tested and normal', 'BRCA1+', 'BRCA2+'].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`GeneticMutation-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="GeneticMutation"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleChange('GeneticMutation', 'value', e.target.value)}
                                    checked={medicalHistory.GeneticMutation?.value === value}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`GeneticMutation-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Ashkenazi Jewish Ancestry */}
                <div className="form-group mb-4">
                    <label className="form-label">
                        Do you have Ashkenazi Jewish ancestry?
                        {isYes(medicalHistory.AJAncestry?.value) && (
                            <i
                                className={`fas fa-flag ml-2 ${medicalHistory.AJAncestry?.flagYN === 1 ? 'color-pink' : 'color-light-grey'} ${!disabled ? 'flag-clickable' : ''}`}
                                onClick={() => handleFlagToggle('AJAncestry', medicalHistory.AJAncestry?.flagYN)}
                                role={disabled ? undefined : "button"}
                                tabIndex={disabled ? undefined : 0}
                                title={medicalHistory.AJAncestry?.flagYN === 1 ? 'Remove flag' : 'Add flag'}
                            />
                        )}
                    </label>
                    <div className="mt-2">
                        {[RESPONSE_VALUES.NO, RESPONSE_VALUES.YES].map((value) => (
                            <div key={value} className="custom-control custom-radio custom-control-inline">
                                <input
                                    type="radio"
                                    id={`AJAncestry-${value.replace(/[^a-zA-Z0-9]/g, '')}`}
                                    name="AJAncestry"
                                    className="custom-control-input"
                                    value={value}
                                    onChange={(e) => handleRadioChange('AJAncestry', e.target.value)}
                                    checked={isChecked(value, medicalHistory.AJAncestry?.value)}
                                    disabled={disabled}
                                />
                                <label className="custom-control-label" htmlFor={`AJAncestry-${value.replace(/[^a-zA-Z0-9]/g, '')}`}>
                                    {value}
                                </label>
                            </div>
                        ))}
                    </div>
                </div>
            </Card.Body>
        </Card>
    );
};

export default PatientMedicalDetailsCard;
